/**
 * One shape for a frame's card and its opened window (Part 10, Rule 1).
 * The wall's height estimate (`components/wall/wall-card-height.ts`) and
 * the opened size (`lib/focus/focus-opened-size.ts`) both read it, so the
 * card and the window cannot drift.
 *
 * - **media** (image, HTML, a PDF file): the card's aspect, edge to edge.
 * - **reading**: a column of text at the opened type.
 * - **object**: the card's em layout, set larger.
 * - **tool**: a live tool at its created size.
 */
import type { FrameThumbnailTheme } from "@/api/frame-listing";
import type { FrameType } from "@/api/spaces";
import { fileCardShape } from "@/components/shapes/file-frame/file-card-shape";
import { htmlCardRatio } from "@/components/shapes/html-frame/html-card-shape";
import { firstCardImage } from "@/components/shapes/image-frame/image-card-content";
import type { FrameData } from "@/lib/frame-data";
import { previewSource } from "@/lib/frame-preview-source";
import { isPdfFile } from "@/lib/pdf-document";
import { currentThemeChoice } from "@/lib/theme";

export type FrameCardFamily = "media" | "reading" | "object" | "tool";

export interface FrameCardShape {
  family: FrameCardFamily;
  /**
   * Width / height, when the card's height follows its width: every media
   * card, and the cover of a link or a (non-PDF) file.
   */
  aspect?: number;
}

/** The opened type of the reading and media families, in px. */
export const OPEN_FS = 17;
/** The opened type of the object family, in px: the card's em layout at 1.5×. */
export const OBJECT_OPEN_FS = 24;
/** A reading column's width in em of `OPEN_FS` (680px). */
export const READING_OPEN_EM = 40;
/** An object's width in em of `OBJECT_OPEN_FS`: the 4-column card (306px at 16.5px). */
export const OBJECT_OPEN_EM = 18.5;
/**
 * What an opened object adds below its card, in em of `OBJECT_OPEN_FS`.
 * An event adds its line only when it has a detail (`objectExtraEm`).
 */
const OBJECT_EXTRA_EM: Partial<Record<FrameType, number>> = {
  /** Each day's sky and low. */
  weather: 2.2,
  /** The bar labels and the insight. */
  chart: 2.6,
  /** The detail line. */
  event: 1.4,
  /** The details row. */
  flight: 3.4,
  /** The Spotify player in place of the play mark. */
  music: 1.6,
  /** Each link's row: title, description and domain. */
  link_preview: 4.6,
  /** The Open button. */
  file: 2.4,
};

const READING_TYPES: ReadonlySet<string> = new Set([
  "doc",
  "markdown",
  "note",
  "conversation",
  "code",
  "terminal",
  "todo",
  "agenda",
  "calendar",
]);

const OBJECT_TYPES: ReadonlySet<string> = new Set([
  "weather",
  "chart",
  "event",
  "flight",
  "music",
  "link_preview",
  "file",
  "preview_card",
]);

/** Types without a cap: a title and a few lines, in em. */
const DEFAULT_PREVIEW_EM = 11;

/** Regular preview heights in em of the card's type: the type's cap or rows. */
const PREVIEW_EM: Partial<Record<FrameType, number>> = {
  /** Padding, a two-line title and up to 17em of lines (`doc-card-preview.tsx`). */
  doc: 22,
  markdown: 22,
  note: 22,
  chart: 15,
  /** The title, the row count and five short rows. */
  dataset: 12,
  /** The title and about four rows. */
  todo: 14,
  code: 15,
  terminal: 7.5,
  github: 15,
  preview_card: 20,
  weather: 15,
  event: 15,
  agenda: 15,
  calendar: 15,
  flight: 15,
  music: 15,
  conversation: 13,
  taskboard: 9,
  memory: 9,
  connection: 9,
};

/** Images without a recorded size, and their placeholders, are 4:3. */
const IMAGE_ASPECT = 4 / 3;
/** The link cover (`aspect-[1.15]` in its preview). */
const LINK_ASPECT = 1.15;

/**
 * The family of `frameType`. A file is media when it is a PDF (read from
 * `frame`'s card data), else an object.
 */
export function frameCardFamily(
  frameType: string | undefined,
  frame?: FrameData,
): FrameCardFamily {
  if (frameType === "image" || frameType === "html") return "media";
  if (frameType === "file") {
    return frame && isPdfFile(previewSource(frame)) ? "media" : "object";
  }
  if (!frameType) return "tool";
  if (READING_TYPES.has(frameType)) return "reading";
  return OBJECT_TYPES.has(frameType) ? "object" : "tool";
}

/** The card of `frame` in `theme` (an HTML poster's ratio is per theme). */
export function frameCardShape(
  frame: FrameData,
  theme: FrameThumbnailTheme = currentThemeChoice(),
): FrameCardShape {
  const family = frameCardFamily(frame.frameType, frame);
  switch (frame.frameType) {
    case "image":
      return {
        family,
        aspect:
          firstCardImage(previewSource(frame).images)?.aspect ?? IMAGE_ASPECT,
      };
    case "html":
      return { family, aspect: htmlCardRatio(frame, theme) };
    case "file":
      return { family, aspect: fileCardShape(frame).ratio };
    case "link_preview":
      return { family, aspect: LINK_ASPECT };
    default:
      return { family };
  }
}

/** A card's regular preview height in em of the wall's type. */
export function cardPreviewEm(frameType: string | undefined): number {
  return (
    (frameType ? PREVIEW_EM[frameType as FrameType] : undefined) ??
    DEFAULT_PREVIEW_EM
  );
}

/** What `frameType` adds below its card when opened, in em. */
export function objectExtraEm(
  frameType: string,
  source: Record<string, unknown>,
): number {
  if (frameType === "event") {
    const detail = source.detail;
    return typeof detail === "string" && detail.trim()
      ? (OBJECT_EXTRA_EM.event ?? 0)
      : 0;
  }
  return OBJECT_EXTRA_EM[frameType as FrameType] ?? 0;
}
